import { type QueryKey } from '@tanstack/react-query';
import { memo, type ReactElement, useMemo, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useBuildByIdQuery, useDeleteBuild, useUpdateBuild } from '@/features/builds/hooks/builds';
import { resolveBuildRunNumber } from '@/features/builds/utils/buildRunNumber';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useDeletePipelineExecution } from '@/features/pipelines/hooks/pipelines';
import { resolvePipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import {
  buildStageDetailPath,
  buildStageExecutionDetailPath,
} from '@/features/pipelines/utils/stagePaths';
import { useProjectFilter } from '@/features/projects';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import { getBuildStatusIconComponent } from '@/features/shared/components/tableSystem/buildStatusIcons';
import { TableBuildStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableBuildStatusBreakdownCell';
import {
  TableActionsCell,
  TableCellContainer,
} from '@/features/shared/components/tableSystem/TableCells';
import {
  TABLE_ROW_HEIGHT,
  TABLE_ROW_MEASURE_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import {
  resolveLatestChipBackground,
  resolveLatestChipForeground,
} from '@/features/shared/components/tableSystem/TableLatestCellStyles';
import {
  CLONE_META_DETAIL_GLYPH_SX,
  TableStatusMetaContent,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { TableStageStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableStageStatusBreakdownCell';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { useDetailEditEditor } from '@/features/shared/hooks/useDetailEditEditor';
import {
  resolveCiStatusLabelKey,
  resolvePreferredCiDisplayStatus,
} from '@/features/shared/utils/ciStatus';
import { useNotifications } from '@/providers/notificationHooks';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDetailLabel } from '@/shared/copy/details';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { getToastMessage } from '@/shared/copy/toasts';
import { useApiErrorMessage } from '@/shared/data/errors';
import { formatDuration, formatStatusLabel } from '@/ui';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { RunLinkCell, TableNameCell } from '@/ui/composites/table';
import { resolveIteratorLabel } from '@/ui/composites/table/cellContents/shared';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { Box, resolveStatusChipTone, StagesIcon } from '@/ui/primitives';
import { EventOutlinedIcon, TimerOutlinedIcon } from '@/ui/primitives/icons';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { SummaryTooltipItem } from '@/ui/composites/table/tableUtils';
import type { DataGridProps, GridColDef } from '@/ui/primitives';
import type { BuildStatus } from '@flowqa/shared';
import type {
  PipelineRunSummaryItemDto,
  QueryResult,
  QueryResult as GridQueryResult,
} from '@flowqa/shared';

type PipelineRunsTableProps = {
  noRowsText?: string;
  persistKey?: string | null;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  showToolbar?: boolean;
  queryFn: (input: GridQueryInput) => Promise<QueryResult<PipelineRunSummaryItemDto>>;
  queryKey: QueryKey;
  initialData?: GridQueryResult<PipelineRunSummaryItemDto>;
  search?: string;
  onQueryMeta?: (meta: { total: number; items: PipelineRunSummaryItemDto[] } | null) => void;
  columnVisibilityModel?: Record<string, boolean>;
  onColumnVisibilityModelChange?: DataGridProps['onColumnVisibilityModelChange'];
  showPipelineColumn?: boolean;
};

const mapPipelineRunJobSummaryItems = (row: PipelineRunSummaryItemDto): SummaryTooltipItem[] =>
  (row.jobSummaries ?? []).map((job) => {
    const runNumber = resolveBuildRunNumber(job.buildKey ?? null);
    const pipelineRunHref = buildRunHref(row);

    return {
      key: job.id,
      detailLabel: row.pipelineName,
      executionLabel: runNumber === null ? null : `#${runNumber}`,
      href: `/pipelines/${resolvePipelineSlug({ id: row.pipelineId, name: row.pipelineName })}`,
      executionHref: pipelineRunHref,
      runNumber,
      status: job.status,
      timestamp: job.timestamp,
    };
  });

const mapPipelineRunStageSummaryItems = (row: PipelineRunSummaryItemDto): SummaryTooltipItem[] => {
  const runNumber = resolveBuildRunNumber(row.buildKey ?? null);

  return (row.stageSummaries ?? []).map((stage) => ({
    key: stage.id,
    detailLabel: stage.stageName,
    executionLabel: runNumber === null ? null : `#${runNumber}`,
    href: buildStageDetailPath({
      pipelineId: row.pipelineId,
      stageName: stage.stageName,
    }),
    executionHref: buildStageExecutionDetailPath({
      stageExecutionId: stage.id,
      pipelineId: row.pipelineId,
      stageName: stage.stageName,
    }),
    runNumber,
    status: stage.status,
    timestamp: stage.timestamp,
  }));
};

const buildRunHref = (row: PipelineRunSummaryItemDto): string | null => {
  const runNumber = resolveBuildRunNumber(row.buildKey);
  if (runNumber === null) {
    return null;
  }

  return `/pipelines/${resolvePipelineSlug({ id: row.pipelineId, name: row.pipelineName })}/runs/${runNumber}`;
};

const PipelineRunsTableComponent = ({
  noRowsText = getEmptyStateMessage('entity.noneFound', { entity: 'pipelineRun' }),
  persistKey,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  hideFooter,
  showToolbar = false,
  queryFn,
  queryKey,
  initialData,
  search,
  onQueryMeta,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  showPipelineColumn = true,
}: PipelineRunsTableProps): ReactElement => {
  const { session } = useAuth();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();
  const updateBuild = useUpdateBuild();
  const deleteBuild = useDeleteBuild();
  const deletePipelineExecution = useDeletePipelineExecution();
  const [editTarget, setEditTarget] = useState<PipelineRunSummaryItemDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PipelineRunSummaryItemDto | null>(null);
  const editTargetBuildQuery = useBuildByIdQuery(editTarget?.id ?? null);
  const editTargetBuild = editTargetBuildQuery.data;
  const editEditor = useDetailEditEditor<BuildStatus>({
    projectId: editTargetBuild?.projectId ?? selectedProjectId,
    successMessage: 'Pipeline run updated',
    saveEntity: async ({ tags, status, timestamp, durationMs }) => {
      if (!editTargetBuild) {
        return;
      }

      const startedAt = timestamp ? timestamp.toISOString() : null;
      const finishedAt =
        timestamp && durationMs !== null && durationMs !== undefined
          ? new Date(timestamp.getTime() + durationMs).toISOString()
          : null;

      await updateBuild.mutateAsync({
        id: editTargetBuild.id,
        tags,
        status,
        startedAt,
        finishedAt,
      });
    },
  });

  const columns = useMemo<Array<GridColDef<PipelineRunSummaryItemDto>>>(() => {
    const pipelineColumn: GridColDef<PipelineRunSummaryItemDto> = {
      field: 'pipelineName',
      headerName: getDetailLabel('pipeline'),
      flex: 0.9,
      align: 'left',
      headerAlign: 'left',
      cellClassName: 'table-name-cell',
      sortable: false,
      filterable: false,
      renderCell: (params): ReactElement => (
        <TableCellContainer gap="none">
          <TableNameCell
            primary={{
              label: params.row.pipelineName,
              href: `/pipelines/${resolvePipelineSlug({
                id: params.row.pipelineId,
                name: params.row.pipelineName,
              })}`,
            }}
          />
        </TableCellContainer>
      ),
    };

    const runColumn: GridColDef<PipelineRunSummaryItemDto> = {
      field: 'buildKey',
      headerName: getDetailLabel('run'),
      flex: 0.7,
      align: 'left',
      headerAlign: 'left',
      cellClassName: 'table-run-link-cell',
      sortable: false,
      filterable: false,
      renderCell: (params): ReactElement => {
        const runNumber = resolveBuildRunNumber(params.row.buildKey);
        const label = runNumber === null ? 'View' : resolveIteratorLabel(runNumber);

        return (
          <TableCellContainer gap="none">
            <RunLinkCell
              label={label}
              href={buildRunHref(params.row) ?? '/pipelines'}
              latest={params.row.isLatest === true}
            />
          </TableCellContainer>
        );
      },
    };

    const statusColumn: GridColDef<PipelineRunSummaryItemDto> = {
      field: 'status',
      headerName: getDetailLabel('status'),
      flex: 0.8,
      align: 'left',
      headerAlign: 'left',
      cellClassName: 'table-status-meta-cell',
      sortable: false,
      filterable: false,
      renderCell: (params): ReactElement => {
        const displayStatus =
          resolvePreferredCiDisplayStatus({
            status: params.row.status,
            lifecycleState: params.row.lifecycleState,
            conclusion: params.row.conclusion,
          }) ?? params.row.status;
        const statusTone = resolveStatusChipTone(displayStatus);
        const StatusIcon = getBuildStatusIconComponent(displayStatus);
        const timestamp = params.row.timestamp;
        const timestampLabel = timestamp ? formatRelativeTime(timestamp) : '';
        const timestampTitle = timestamp ? formatDate(timestamp, { includeTime: true }) : '';

        return (
          <TableCellContainer gap="none">
            <TableStatusMetaContent
              statusLabel={formatStatusLabel(resolveCiStatusLabelKey(displayStatus))}
              statusHref={buildRunHref(params.row)}
              chipTone={statusTone}
              chipIcon={<Box component={StatusIcon} />}
              chipBackgroundColor={(theme) => resolveLatestChipBackground(theme, statusTone)}
              chipForegroundColor={(theme) => resolveLatestChipForeground(theme, statusTone)}
              durationLabel={formatDuration(params.row.durationMs)}
              durationIcon={<TimerOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
              timestampLabel={timestampLabel}
              timestampTitle={timestampTitle}
              timestampIcon={<EventOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
            />
          </TableCellContainer>
        );
      },
    };

    const stagesColumn: GridColDef<PipelineRunSummaryItemDto> = {
      field: 'stageStatusCounts',
      headerName: 'Stages',
      flex: 0.7,
      align: 'left',
      headerAlign: 'left',
      cellClassName: 'table-breakdown-cell',
      sortable: false,
      filterable: false,
      renderCell: (params): ReactElement => (
        <TableCellContainer gap="none">
          <TableStageStatusBreakdownCell
            total={params.row.stageTotal ?? 0}
            counts={params.row.stageStatusCounts}
            summaryItems={mapPipelineRunStageSummaryItems(params.row)}
          />
        </TableCellContainer>
      ),
    };

    const jobsColumn: GridColDef<PipelineRunSummaryItemDto> = {
      field: 'jobStatusCounts',
      headerName: 'Jobs',
      flex: 0.7,
      align: 'left',
      headerAlign: 'left',
      cellClassName: 'table-breakdown-cell',
      sortable: false,
      filterable: false,
      renderCell: (params): ReactElement => (
        <TableCellContainer gap="none">
          <TableBuildStatusBreakdownCell
            total={params.row.jobTotal ?? 0}
            counts={params.row.jobStatusCounts}
            summaryItems={mapPipelineRunJobSummaryItems(params.row)}
            totalIcon={StagesIcon}
            showDateHeaders={false}
          />
        </TableCellContainer>
      ),
    };

    const actionsColumn: GridColDef<PipelineRunSummaryItemDto> = {
      field: 'actions',
      headerName: '',
      flex: 0.1,
      minWidth: 56,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params): ReactElement => (
        <TableActionsCell
          rowName={params.row.buildKey ?? params.row.id}
          editActionLabel={getEntityActionLabel('edit', 'Pipeline Run')}
          deleteActionLabel={getEntityActionLabel('delete', 'Pipeline Run')}
          onEdit={() => {
            setEditTarget(params.row);
          }}
          onDelete={() => {
            setDeleteTarget(params.row);
          }}
          disableEdit={
            editEditor.isPending || deleteBuild.isPending || deletePipelineExecution.isPending
          }
          disableDelete={deleteBuild.isPending || deletePipelineExecution.isPending}
        />
      ),
    };

    return showPipelineColumn
      ? [pipelineColumn, runColumn, statusColumn, stagesColumn, jobsColumn, actionsColumn]
      : [runColumn, statusColumn, stagesColumn, jobsColumn, actionsColumn];
  }, [
    deleteBuild.isPending,
    deletePipelineExecution.isPending,
    editEditor.isPending,
    showPipelineColumn,
  ]);

  const editTargetStartedAt = editTargetBuild?.startedAt ?? null;
  const editTargetFinishedAt = editTargetBuild?.finishedAt ?? null;
  const editTargetDurationMs =
    !editTargetStartedAt || !editTargetFinishedAt
      ? null
      : Math.max(editTargetFinishedAt.getTime() - editTargetStartedAt.getTime(), 0);

  return (
    <>
      <ServerDataTable<PipelineRunSummaryItemDto>
        queryPath="pipeline-executions/summary"
        initialData={initialData}
        queryFn={queryFn}
        queryKey={queryKey}
        columns={columns}
        getRowId={(row) => row.id}
        isAuthenticated={Boolean(session?.userId)}
        organisationId={selectedOrganisationId}
        projectId={selectedProjectId}
        pageSizeOptions={pageSizeOptions}
        initialPageSize={initialPageSize}
        search={search}
        columnVisibilityModel={columnVisibilityModel}
        disablePageSizeSelect={disablePageSizeSelect}
        hideFooter={hideFooter}
        disableColumnMenu
        disableColumnSorting
        showToolbar={showToolbar}
        showColumnVerticalBorder={false}
        showCellVerticalBorder={false}
        noRowsText={noRowsText}
        interactionMode="static"
        getRowHeight={() => 'auto'}
        skeletonRowHeight={TABLE_ROW_HEIGHT}
        skeletonGetRowHeight={null}
        autoHeight
        onQueryMeta={onQueryMeta}
        gridProps={{
          getEstimatedRowHeight: () => TABLE_ROW_MEASURE_HEIGHT,
          onColumnVisibilityModelChange,
        }}
        sx={tableSx}
        persistKey={persistKey}
      />
      <DetailEditEntityDialog
        open={editTarget !== null}
        entityLabel="Pipeline Run"
        entityName={editTarget?.buildKey ?? editTarget?.id ?? ''}
        projectId={editTargetBuild?.projectId ?? selectedProjectId}
        tags={editTargetBuild?.tags ?? []}
        status={editTargetBuild?.status}
        statusOptions={[
          { value: 'queued', label: formatStatusLabel('queued') },
          { value: 'running', label: formatStatusLabel('running') },
          { value: 'completed', label: formatStatusLabel('completed') },
          { value: 'failed', label: formatStatusLabel('failed') },
        ]}
        timestamp={editTargetStartedAt}
        durationMs={editTargetDurationMs}
        timestampLabel="Started"
        durationLabel="Duration"
        durationInputMode="timeParts"
        loading={editEditor.isPending || (editTarget !== null && editTargetBuildQuery.isPending)}
        cascadeCopy="Tags will be applied only to this pipeline run."
        onClose={() => {
          if (editEditor.isPending) {
            return;
          }

          setEditTarget(null);
        }}
        onSave={async ({ tags, colors, status, timestamp, durationMs }) => {
          await editEditor.save({ tags, colors, status, timestamp, durationMs });
          setEditTarget(null);
        }}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'pipelineRun' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'pipelineRun',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'pipelineRun',
        })}
        confirmColor="red"
        loading={deleteBuild.isPending || deletePipelineExecution.isPending}
        onClose={() => {
          if (deleteBuild.isPending || deletePipelineExecution.isPending) {
            return;
          }

          setDeleteTarget(null);
        }}
        onConfirm={() => {
          if (!deleteTarget) {
            return;
          }

          const deletedRunName = deleteTarget.buildKey ?? deleteTarget.id;
          const deleteMutation = deleteTarget.pipelineExecutionId
            ? deletePipelineExecution
            : deleteBuild;
          deleteMutation.mutate(
            {
              id: deleteTarget.pipelineExecutionId ?? deleteTarget.id,
              projectId: selectedProjectId,
            },
            {
              onSuccess: () => {
                notify({
                  message: getToastMessage('entity.deleted', {
                    entity: 'pipeline run',
                    name: deletedRunName,
                  }),
                  tone: 'green',
                });
                setDeleteTarget(null);
              },
              onError: (error) => {
                notify({ message: toErrorMessage(error), tone: 'red' });
              },
            },
          );
        }}
      />
    </>
  );
};

export const PipelineRunsTable = memo(PipelineRunsTableComponent);
